import _ from "lodash";
import tinyColor from "tinycolor2";
import plotCharts from "./PlotCharts";
import sweeper from "./sweeper";
import { DateTime } from "luxon";

export default function _generateCanvasWorker(obj) {
  return new CanvasWorker(obj);
}
class CanvasWorker {
  constructor(obj) {
    Object.assign(this, generateInstance(obj));
  }
}
function generateInstance({ id } = {}) {
  /**
   * Paints the canvas.
   */
  function paintCanvas(frameMs) {
    if (!state.visible) return pause();
    if (state.transition) {
      if (state.debug) console.log("transition in progress");
      return pause();
    }
    if (state.dropped) {
      if (state.debug) console.warn("paintCanvas on dropped instance", id);
      return pause();
    }
    if (state.liveMode) {
      if (isNaN(frameMs) && state.animationFrameId)
        cancelAnimationFrame(state.animationFrameId);
      state.animationId = requestAnimationFrame(paintCanvas);
    }
    if (!state.canvas) return console.warn("no canvas for instance", id);
    const now = performance.now() + state.offsetMs;
    const { canvas, ctx } = state;
    if (!canvas) {
      return;
    }

    let gap;
    const renderPoint = (point, index) => {
      if (!(point?.length > 1)) {
        gap = true;
        return;
      }
      if (point[0] > canvas.width) {
        return;
      }
      if (index === 0 || point[2] || gap) {
        gap = false;
        ctx.moveTo(...point);
      } else {
        ctx.lineTo(...point);
      }
    };

    if (!state.isSweeping || state.rescaled) {
      clearCanvas();
    }
    if (state.isSweeping) {
      return sweeper.paintCanvas({ state, now, renderPoint });
    }

    state.waveGroups.forEach((group) => {
      gap = false;
      if (!state.canvasPoints?.[group.key]) {
        return;
      }
      translateGroupPoints({
        group,
        now,
      });
      const lineColor = state.lineColor || group.lineColor;
      ctx.strokeStyle = group.disabled
        ? tinyColor(lineColor).setAlpha(0.4)
        : lineColor;

      ctx.lineWidth = 2;
      ctx.beginPath();

      _.forEach(state.canvasPoints[group.key].points, renderPoint);

      state.canvasPoints[group.key].renderTime = state.liveMode
        ? now
        : undefined;
      ctx.stroke();
    });
  }

  /**
   * Sets the canvas size.
   * @param {Object} p
   * @param {Number} p.width
   * @param {Number} p.height
   */
  function setCanvasSize({ width, height }) {
    const canvas = state.canvas;
    if (!canvas) {
      return;
    }
    const widthInt = Math.round(width);
    const heightInt = Math.round(height);
    const widthChange = widthInt - canvas.width;
    const heightChange = heightInt - canvas.height;
    if (widthChange || heightChange) {
      canvas.width = Math.max(1, widthInt);
      canvas.height = Math.max(1, heightInt);
    }
    if (state.liveMode) {
      // stop the render cycle, until the next plotCharts call, should be immediate.
      pause();
      // reset the rendering position, unless the chart is just getting shorter.
      if (state.isSweeping) sweeper.reset(state);
    }
  }

  function pause() {
    if (state.animationId) {
      cancelAnimationFrame(state.animationId);
      state.animationId = null;
    }
  }

  /**
   * Clears the canvas
   */
  function clearCanvas() {
    state.ctx?.clearRect(0, 0, state.ctx.canvas.width, state.ctx.canvas.height);
  }

  /**
   *
   * @param {Object} p
   * @param {HTMLCanvasElement} p.canvas
   * @param {Array} p.waveGroups
   * @param {number} p.pixelsPerSecond
   * @param {Boolean} sweep
   */
  function init(initialState) {
    const { canvas } = initialState;
    const ctx = canvas.getContext("2d");
    Object.assign(state, {
      ...initialState,
      ctx,
    });
    // sweeperHideDot is a static configuration.
    if (state.sweeperHideDot) sweeper.dotLength = 0;
  }

  /**
   *
   * @param {Object} p
   * @param {Object} canvasPoints
   */
  function setCanvasPoints({ canvasPoints }) {
    state.canvasPoints = canvasPoints;
  }

  /**
   *
   * @param {Object} p
   * @param {Object} group
   * @param {Number} now
   */
  function translateGroupPoints({ group, now }) {
    if (state.dropped)
      return console.error("translatePoints on dropped instance");
    if (!state.canvasPoints[group.key])
      state.canvasPoints[group.key] = { points: [] };
    const waveCanvasPoints = state.canvasPoints[group.key];
    if (state.liveMode && waveCanvasPoints.renderTime) {
      const deltaX =
        ((now - waveCanvasPoints.renderTime) / 1000) * state.pixelsPerSecond;
      waveCanvasPoints.points.forEach((point) => {
        if (point) point[0] = point[0] - deltaX;
      });
      const firstIndex = state.canvasPoints[group.key].points.findIndex(
        (point) => point && point[0] >= -deltaX,
      );
      waveCanvasPoints.points = state.canvasPoints[group.key].points.slice(
        Math.max(firstIndex - 1, 0),
      );
    }
  }

  /**
   * State transitions.
   * @param {Object} _state - The partial state to merge into the current state.
   */
  function assignState(_state) {
    if (_state.canvas) throw "Canvas not accepted here, use init";
    const newState = { ...state, ..._state };
    if (!newState.liveMode) {
      pause();
    }
    if (state.visible && !newState.visible) resetLiveMode();
    if (state.isSweeping && state.canvasPoints && !newState.liveMode) {
      try {
        sweeper.transitionOut(state).then(() => {
          if (state.canvasPoints) paintCanvas();
        });
      } catch (e) {
        console.error("caught transition error:", newState, e);
      }
    } else if (newState.pixelsPerSecond !== state.pixelsPerSecond) {
      if (state.sweep) {
        sweeper.reset(state);
      }
    }
    if (!newState.sweep !== !state.sweep) {
      if (newState.sweep && state.canvasPoints) {
        state.canvasPoints = undefined;
        state.nextCanvasPoints = undefined;
      }
      state.renderRangeX = undefined;
      state.ctx?.restore();
    }

    if (newState.liveMode && !state.liveMode) {
      state.canvasPoints = undefined;
    }
    Object.assign(state, _state);
  }

  /**
   *
   * @param {Object} p
   * @param {String} key
   * @param {Boolean} disabled
   */
  function setWaveDisabled({ key, disabled }) {
    const group = state.waveGroups.find((g) => g.key === key);
    if (group) group.disabled = disabled;
  }

  function _plotCharts(options) {
    if (options.pixelsPerSecond) {
      assignState({ pixelsPerSecond: options.pixelsPerSecond });
    }
    if (!state.canvas) return;
    let canvasEndDateTime = DateTime.fromISO(options.chartEndTime);

    if (state.liveMode && options.chartEndTime) {
      canvasEndDateTime = adjustForDrift({
        chartEndDateTime: canvasEndDateTime,
      });
    }

    state.chartEndDateTime = canvasEndDateTime;

    if (state.isSweeping) {
      sweeper.resetOnTilt({ state, options });
      canvasEndDateTime =
        state.nextCanvasPoints?.chartEndDateTime ||
        state.canvasPoints?.chartEndDateTime ||
        canvasEndDateTime;
    }

    const { height, width } = state.canvas;
    const { canvasPoints } = plotCharts({
      height,
      width,
      waveGroups: state.waveGroups,
      ...options,
      pixelsPerSecond: state.pixelsPerSecond,
      chartEndTime: canvasEndDateTime.toISO(),
    });

    if (state.isSweeping) {
      sweeper.wrapCanvasPoints({ state, canvasPoints });
    } else {
      state.canvasPoints = canvasPoints;
    }
    if (!state.animationId) paintCanvas();
    return { chartPlotted: true };
  }

  function assertFilled({ min = 0.9, intervalMs = 1229, log } = {}) {
    if (log)
      console.log(`assertFilled started ${min * 100}% every ${intervalMs}ms`);
    clearInterval(assertFilled.interval);
    assertFilled.interval = setInterval(() => {
      // This assertion doesn't work with sweep mode
      if (state.isSweeping) return clearInterval(assertFilled.interval);
      Object.values(state.canvasPoints || {}).forEach((wave) => {
        const lastX = _.last(wave.points)?.[0];
        if (lastX) {
          const filled = lastX / state.canvas.width;
          if (log) console.log(`filled to ${(filled * 100).toFixed()}%`);
          if (lastX)
            console.assert(
              filled > min,
              `filled to ${(filled * 100).toFixed()}%, min ${min * 100}%`,
            );
        }
      });
    }, intervalMs);
  }

  function adjustForDrift({ chartEndDateTime }) {
    // adjust for drift if necessary
    const now = performance.now();
    const newChartEndDateTime = state.getLiveChartTime(now);
    if (newChartEndDateTime) {
      const drift = Math.abs(+newChartEndDateTime - chartEndDateTime);
      if (drift < 1500) {
        chartEndDateTime = newChartEndDateTime;
      } else {
        if (state.sweep) sweeper.reset(state);
        console.log(`${drift}ms drift correction `);
      }
    }
    state.chartEndDateTime = chartEndDateTime;
    state.chartEndTimeUpdatedAt = now;
    return chartEndDateTime;
  }

  function destroy() {
    clearInterval(assertFilled.interval);
    clearInterval(assertFrameRate.fpsInterval);
    state.dropped = true;
    state.canvasPoints = undefined;
    return { dropped: true };
  }

  const state = {
    sweeper: {},
    sweep: false,
    offsetMs: 0,
    get isSweeping() {
      return this.sweep && this.liveMode;
    },
    get canvasWidthSeconds() {
      return this.canvas?.width / this.pixelsPerSecond;
    },
    transitionConfig: {
      maxDurationMs: 750,
      easingFunction: "easeInOutQuart",
    },
    getLiveChartTime(now = performance.now()) {
      if (state.chartEndDateTime && state.chartEndTimeUpdatedAt) {
        const lastChartEndDateTime = state.chartEndDateTime;
        const elapsedMs = Math.round(now - state.chartEndTimeUpdatedAt);
        return lastChartEndDateTime.plus(elapsedMs);
      }
    },
    clearCanvas,
  };

  function assertFrameRate({
    minFps = 28,
    sampleFrames = 3,
    intervalMs = 1919,
    log,
  } = {}) {
    if (log) {
      console.log(
        `assertFrameRate started ${minFps}fps for ${sampleFrames}frames every ${intervalMs}ms`,
      );
    }

    clearInterval(assertFrameRate.fpsInterval);
    assertFrameRate.fpsInterval = setInterval(() => {
      if (state.fpsTest) return;
      const frameTimes = [];
      state.fpsTest = () => {
        frameTimes.push(performance.now());
        if (frameTimes.length > sampleFrames) {
          const averageFrameRateMs =
            frameTimes.reduce(
              (sumOfFrameIntervalsMs, frameTimeMs, i, array) => {
                return (
                  i && sumOfFrameIntervalsMs + (frameTimeMs - array[i - 1])
                );
              },
              0,
            ) /
            (frameTimes.length - 1);
          const FPS = 1000 / averageFrameRateMs;
          console.assert(
            FPS >= minFps,
            `frameRate ${FPS}fps below minimum ${minFps}fps`,
          );
          if (log)
            console.log(`frameRate ${FPS.toFixed()}fps | minimum ${minFps}fps`);
          state.FPS = FPS;
          state.fpsTest = undefined;
        }
      };
      (function frameLoop() {
        if (!state.fpsTest) return;
        state.fpsTest();
        requestAnimationFrame(frameLoop);
      })();
    }, intervalMs);
  }

  function resetLiveMode({ force } = {}) {
    if (!state.liveMode && !force) return;
    sweeper.reset(state);
    state.ctx?.restore();
    clearCanvas();
    state.chartEndDateTime = undefined;
    state.chartEndTimeUpdatedAt = undefined;
  }

  return {
    id,
    setCanvasSize,
    clearCanvas,
    init,
    setCanvasPoints,
    assignState,
    setWaveDisabled,
    state,
    plotCharts: _plotCharts,
    assertFrameRate,
    assertFilled,
    destroy,
    resetLiveMode,
  };
}
